'use strict';

// Active Interactions (CRUD)
import { merge } from 'lodash';
import EcsInteraction from '@/components/ecs-interaction/EcsInteraction';
import { NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID } from '@/constants/socket-events';
import { INTERACTION_STATE } from '@/constants/interaction/constants';

const createActiveInteraction = (state, interaction) => {
  state.interactions.active.unshift(interaction);
};

const updateActiveInteraction = (state, interaction) => {
  // Find and update the interaction in place
  const interactionIndex = state.interactions.active.findIndex(i => i.interactionId === interaction.interactionId);
  if (interactionIndex !== -1) {
    const existingInteraction = state.interactions.active[interactionIndex];
    const mergedInteraction = merge(existingInteraction, interaction);
    state.interactions.active.splice(interactionIndex, 1, mergedInteraction);
  }
};

const deleteActiveInteraction = (state, interactionId) => {
  state.interactions.active = state.interactions.active.filter(i => i.interactionId !== interactionId);
};

// Pending Interactions (CRUD)
const createPendingInteraction = (state, interaction) => {
  state.interactions.pending.unshift(interaction);
};

const updatePendingInteraction = (state, interaction) => {
  // Find and update the interaction in place
  const _interactionIndex = state.interactions.pending.findIndex(i => i.interactionId === interaction.interactionId);
  if (_interactionIndex !== -1) {
    const existingInteraction = state.interactions.pending[_interactionIndex];
    const mergedInteraction = merge(existingInteraction, interaction);
    state.interactions.pending.splice(_interactionIndex, 1, mergedInteraction);
  }
};

const deletePendingInteraction = (state, interactionId) => {
  state.interactions.pending = state.interactions.pending.filter(i => i.interactionId !== interactionId);
};

// Historical Interactions (CRUD)
const createHistoricalInteraction = (state, interaction) => {
  state.interactions.history.unshift(interaction);
};

const updateHistoricalInteraction = (state, interaction) => {
  // Find and update the interaction in place
  const _interactionIndex = state.interactions.history.findIndex(i => i.interactionId === interaction.interactionId);
  if (_interactionIndex !== -1) {
    const existingInteraction = state.interactions.history[_interactionIndex];
    const mergedInteraction = merge(existingInteraction, interaction);
    state.interactions.history.splice(_interactionIndex, 1, mergedInteraction);
  }
};

const deleteHistoricalInteraction = (state, interactionId) => {
  state.interactions.history = state.interactions.history.filter(i => i.interactionId !== interactionId);
};

// NOTE: Workaround to solve the bad practices in the ECS RESTful API services :(
const updateInteractionDispositionCode = (state, data) => {
  // Find and update the interaction in place
  const interaction = state.interactions.active.find(i => i.interactionId === data.interactionId);
  if (interaction) {
    interaction.disposition.dispositionCodeId = data.dispositionCodeId;
  }
};

const updateInteractionCrmContactId = (state, data) => {
  // Find and update the interaction in place
  const interaction = [...state.interactions.active, ...state.interactions.history].find(
    i => i.interactionId === data.interactionId
  );
  if (!interaction) {
    return;
  }

  interaction.crm.contactId = data.contactId;
};

const updateInteractionCompletionStatus = (state, data) => {
  const interaction = [...state.interactions.active, ...state.interactions.history].find(
    i => i.interactionId === data.interactionId
  );

  if (!interaction) {
    return;
  }

  interaction.completionStatus = data.completionStatus;
};

const updateInteractionCompletionStatusInformation = (state, data) => {
  const interaction = [...state.interactions.active, ...state.interactions.history].find(
    i => i.interactionId === data.interactionId
  );

  if (!interaction) {
    return;
  }

  interaction.completionStatus.completionStatusInformation = data.completionStatusInformation;
};

const setInterationDestinationForEmail = (state, payload) => {
  const interaction = state.interactions.active.find(i => i.interactionId === payload.interactionId);
  interaction.routing.destination = payload.endPoint;
  interaction.crm.customerFullName = payload.name;
};

const setHistorySince = (state, since) => {
  state.historySince = since;
};

const setAgentInteractionHistory = (state, { agent, businessProcesses, agentInteractions }) => {
  state.interactions.history = agentInteractions.map(interaction => {
    return structuredClone(createInteraction(agent, businessProcesses, interaction));
  });
};

const addAgentInteractionHistory = (state, { agent, businessProcesses, agentInteraction }) => {
  state.interactions.history.push(structuredClone(createInteraction(agent, businessProcesses, agentInteraction)));
};

const addAgentInteractionsToHistory = (state, { agent, businessProcesses, agentInteractions }) => {
  agentInteractions.map(interaction => {
    state.interactions.history.push(structuredClone(createInteraction(agent, businessProcesses, interaction)));
  });
};

// Auxiliar function
const createInteraction = (agent, businessProcesses, interaction) => {
  const currentAgent = {
    isDestination: [interaction.destination, interaction.firstAgent, interaction.lastAgent].includes(agent.name),
    isOriginator: [interaction.origin].includes(agent.name)
  };

  // NOTE: Workaround until we can get the business process from the API
  const businessProcess = businessProcesses.find(bp => bp.id === interaction.businessProcessId) || {
    id: null,
    name: interaction.lastBusinessProcess || ''
  };

  // Fallback for missing interaction type from API /Contacts/action/GetAgentContactInteractionsHistory
  let interactionType = interaction.interactionType;
  if (!interactionType && interaction.interactionMediaType) {
    switch (interaction.interactionMediaType.toUpperCase()) {
      case EcsInteraction.mediaTypes.telephony:
        interactionType = EcsInteraction.interactionTypes.incomingTelephony;
        break;
      case EcsInteraction.mediaTypes.chat:
        interactionType = EcsInteraction.interactionTypes.incomingChat;
        break;
      case EcsInteraction.mediaTypes.email:
        interactionType = EcsInteraction.interactionTypes.incomingEmail;
        break;
      case EcsInteraction.mediaTypes.sms:
        interactionType = EcsInteraction.interactionTypes.incomingSMS;
        break;
    }
  }

  let interactionMediaType = null;
  if (interaction.interactionMediaType) {
    interactionMediaType = interaction.interactionMediaType;
  } else if (
    interaction.interactionType.toUpperCase() === EcsInteraction.interactionTypes.internalChat ||
    interaction.interactionType.toUpperCase() === EcsInteraction.interactionTypes.incomingChat
  ) {
    interactionMediaType = EcsInteraction.mediaTypes.chat;
  } else if (
    [
      EcsInteraction.interactionTypes.internalCall,
      EcsInteraction.interactionTypes.externalCall,
      EcsInteraction.interactionTypes.incomingTelephony,
      EcsInteraction.interactionTypes.callbackTelephony,
      EcsInteraction.interactionTypes.campaignTelephony
    ].includes(interaction.interactionType.toUpperCase())
  ) {
    interactionMediaType = EcsInteraction.mediaTypes.telephony;
  } else if (
    interaction.interactionType.toUpperCase() === EcsInteraction.interactionTypes.incomingEmail ||
    interaction.interactionType.toUpperCase() === EcsInteraction.interactionTypes.outgoingEmail
  ) {
    interactionMediaType = EcsInteraction.mediaTypes.email;
  } else if (
    interaction.interactionType.toUpperCase() === EcsInteraction.interactionTypes.incomingSMS ||
    interaction.interactionType.toUpperCase() === EcsInteraction.interactionTypes.outgoingSMS
  ) {
    interactionMediaType = EcsInteraction.mediaTypes.sms;
  }

  return new EcsInteraction({
    ...interaction,

    interactionStateId: INTERACTION_STATE.handled,
    interactionStateName: '',

    origin: currentAgent.isOriginator ? agent.endPoint : interaction.origin,
    destination: currentAgent.isDestination ? agent.endPoint : interaction.destination,

    links: [],
    completionStatuses: [],

    interactionType,
    interactionMediaType,

    dispositionCodeId:
      interaction.dispositionCodeName === 'Not Selected Timeout' ? NOT_SELECTED_TIMEOUT_DISPOSITION_CODE_ID : null,

    completionStatus: {
      completionStatusName: interaction.status
    },

    businessProcessId: businessProcess ? businessProcess.id : null,
    businessProcessName: businessProcess ? businessProcess.name : null,

    wrapUpTime: interaction.wrapTime,
    waitingTime: interaction.waitTime,
    timestamp: interaction.startTime || interaction.entryTime
  });
};

export default {
  createPendingInteraction,
  updatePendingInteraction,
  deletePendingInteraction,
  createActiveInteraction,
  updateActiveInteraction,
  deleteActiveInteraction,
  createHistoricalInteraction,
  updateHistoricalInteraction,
  deleteHistoricalInteraction,
  updateInteractionDispositionCode,
  updateInteractionCrmContactId,
  updateInteractionCompletionStatus,
  updateInteractionCompletionStatusInformation,
  setInterationDestinationForEmail,
  setAgentInteractionHistory,
  addAgentInteractionHistory,
  addAgentInteractionsToHistory,
  setHistorySince
};
